import type {
  HealthCheckAction,
  HealthCheckActionKind,
  HealthCheckFindingDto,
  HealthCheckInternalRouteTarget,
} from '../models/healthCheck.types'

import { CONNECTIONS_ROUTE } from '@/features/integrations'

const LINKABLE_SCHEMES = ['http:', 'https:'] as const
const INTERNAL_ROUTE_TARGETS = {
  connections: CONNECTIONS_ROUTE,
} as const satisfies Record<string, HealthCheckInternalRouteTarget>

type ActionSource = {
  action?: HealthCheckFindingDto['action'] | undefined
  action_url?: HealthCheckFindingDto['action_url'] | undefined
  action_target_kind?: HealthCheckFindingDto['action_target_kind'] | undefined
  action_route_intent?: HealthCheckFindingDto['action_route_intent'] | undefined
}

type UrlActionKind = Exclude<HealthCheckActionKind, 'internalRoute'>

/** Deny-by-default gate for the server-authored remediation target (contract 21 §14 E6/E6b). */
export function classifyActionUrl(value: unknown): UrlActionKind | null {
  if (typeof value !== 'string') return null
  const target = value.trim()
  if (!target) return null
  if (target.startsWith('//')) return 'externalLink'
  if (target.startsWith('/')) return 'apiPost'

  try {
    const protocol = new URL(target).protocol
    return (LINKABLE_SCHEMES as readonly string[]).includes(protocol) ? 'externalLink' : 'unsafe'
  } catch {
    return 'unsafe'
  }
}

function toLegacyAction(label: unknown, target: unknown): HealthCheckAction | null {
  const kind = classifyActionUrl(target)
  if (!kind || typeof target !== 'string') return null
  return {
    label: typeof label === 'string' && label.trim() ? label.trim() : null,
    kind,
    target: target.trim(),
  }
}

function internalRouteTarget(intent: unknown): HealthCheckInternalRouteTarget | null {
  if (typeof intent !== 'string') return null
  return Object.prototype.hasOwnProperty.call(INTERNAL_ROUTE_TARGETS, intent)
    ? INTERNAL_ROUTE_TARGETS[intent as keyof typeof INTERNAL_ROUTE_TARGETS]
    : null
}

function typedUrlAction(
  label: string | null,
  target: unknown,
  expected: 'apiPost' | 'externalLink',
): HealthCheckAction | null {
  const classified = classifyActionUrl(target)
  if (!classified || typeof target !== 'string') return null
  return {
    label,
    kind: classified === expected ? expected : 'unsafe',
    target: target.trim(),
  }
}

/**
 * Maps the whole finding action contract. A recognised typed kind owns dispatch; only an absent,
 * null, or future unknown kind follows the legacy URL classifier during the rollout.
 */
export function toAction(dto: ActionSource): HealthCheckAction | null {
  const label = typeof dto.action === 'string' && dto.action.trim() ? dto.action.trim() : null

  if (dto.action_target_kind === 'internal_route') {
    const target = internalRouteTarget(dto.action_route_intent)
    return target
      ? { label, kind: 'internalRoute', target }
      : {
          label,
          kind: 'unsafe',
          target:
            typeof dto.action_route_intent === 'string'
              ? `internal_route:${dto.action_route_intent}`
              : 'internal_route:',
        }
  }
  if (dto.action_target_kind === 'api_post') {
    return typedUrlAction(label, dto.action_url, 'apiPost')
  }
  if (dto.action_target_kind === 'external_link') {
    return typedUrlAction(label, dto.action_url, 'externalLink')
  }

  return toLegacyAction(dto.action, dto.action_url)
}

export class UnsafeActionTargetError extends Error {
  readonly i18nKey = 'healthChecks.action.unsafeTarget'

  constructor(readonly target: string) {
    super(`Refused unsafe health-check action target: ${target}`)
    this.name = 'UnsafeActionTargetError'
  }
}
